'use client';

import { Award, BookOpen, Clock, TrendingUp } from 'lucide-react';
import type { UserProgress, LevelInfo } from '@/lib/learning-progress';

interface ProgressTrackerProps {
  progress: UserProgress;
  levelInfo: LevelInfo;
  showDetails?: boolean;
}

export default function ProgressTracker({
  progress,
  levelInfo,
  showDetails = false,
}: ProgressTrackerProps) {
  const progressPercentage = (levelInfo.storiesCompleted / levelInfo.storiesRequired) * 100;

  return (
    <div
      style={{
        background: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: '24px',
        padding: 'clamp(1.5rem, 3vw, 2rem)',
        border: '2px solid rgba(255, 255, 255, 0.8)',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
      }}
    >
      {/* Level Header - Enhanced */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'clamp(1.25rem, 3vw, 1.5rem)', flexWrap: 'wrap', gap: 'clamp(0.75rem, 2vw, 1rem)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(0.75rem, 2vw, 1rem)' }}>
          <div
            style={{
              width: 'clamp(56px, 8vw, 64px)',
              height: 'clamp(56px, 8vw, 64px)',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(255, 215, 0, 0.4), 0 0 0 3px rgba(255, 255, 255, 0.8)',
              animation: 'pulse 3s ease-in-out infinite',
            }}
          >
            <Award size={28} color="white" strokeWidth={2.5} />
          </div>
          <div>
            <div style={{ fontSize: 'clamp(0.875rem, 2vw, 0.9375rem)', color: '#64748b', fontWeight: 700, marginBottom: '0.25rem' }}>
              Current Level
            </div>
            <div style={{ 
              fontSize: 'clamp(1.5rem, 4vw, 2rem)', 
              fontWeight: 900, 
              background: 'linear-gradient(135deg, #FFD700, #FFA500)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              lineHeight: 1 
            }}>
              Level {progress.currentLevel}
            </div>
          </div>
        </div>
        <div
          style={{
            background: 'linear-gradient(135deg, #B4A0E5, #A8D8E6)',
            color: 'white',
            padding: 'clamp(0.625rem, 2vw, 0.75rem) clamp(1rem, 2.5vw, 1.25rem)',
            borderRadius: '14px',
            fontSize: 'clamp(0.875rem, 2vw, 0.9375rem)',
            fontWeight: 800,
            boxShadow: '0 4px 16px rgba(180, 160, 229, 0.3)',
          }}
        >
          {levelInfo.title}
        </div>
      </div>

      {/* Progress Bar - Enhanced with Animation */}
      <div style={{ marginBottom: 'clamp(1.25rem, 3vw, 1.5rem)' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'clamp(0.625rem, 1.5vw, 0.75rem)',
            flexWrap: 'wrap',
            gap: '0.5rem',
          }}
        >
          <div style={{ fontSize: 'clamp(0.875rem, 2vw, 0.9375rem)', color: '#64748b', fontWeight: 700 }}>
            Progress to Level {levelInfo.nextLevel || progress.currentLevel + 1}
          </div>
          <div style={{ 
            fontSize: 'clamp(0.875rem, 2vw, 1rem)', 
            background: 'linear-gradient(135deg, #B4A0E5, #A8D8E6)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 900,
          }}>
            {levelInfo.storiesCompleted} / {levelInfo.storiesRequired} stories
          </div>
        </div>
        <div
          style={{
            width: '100%',
            height: 'clamp(14px, 2vw, 16px)',
            background: 'linear-gradient(135deg, #F0F9FF, #E0F2FE)',
            borderRadius: '12px',
            overflow: 'hidden',
            position: 'relative',
            boxShadow: '0 2px 8px rgba(168, 216, 230, 0.2), 0 0 0 2px rgba(255, 255, 255, 0.8)',
            border: '1px solid rgba(168, 216, 230, 0.3)',
          }}
        >
          <div
            style={{
              width: `${Math.min(progressPercentage, 100)}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #C9B6E4 0%, #A8D8E6 50%, #B4A0E5 100%)',
              backgroundSize: '200% 100%',
              borderRadius: '12px',
              transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
              animation: 'shimmer 3s ease-in-out infinite',
              boxShadow: '0 0 20px rgba(180, 160, 229, 0.5)',
            }}
          />
        </div>
        {/* Percentage Badge */}
        <div style={{
          textAlign: 'center',
          marginTop: 'clamp(0.5rem, 1vw, 0.75rem)',
        }}>
          <span style={{
            display: 'inline-block',
            padding: 'clamp(0.375rem, 1vw, 0.5rem) clamp(0.875rem, 2vw, 1.125rem)',
            background: 'linear-gradient(135deg, #FFF5EB, #FEF3E2)',
            border: '2px solid rgba(248, 200, 160, 0.4)',
            borderRadius: '12px',
            fontSize: 'clamp(0.875rem, 2vw, 1rem)',
            fontWeight: 800,
            color: '#92400E',
            boxShadow: '0 2px 8px rgba(248, 200, 160, 0.2)',
          }}>
            {Math.round(progressPercentage)}% Complete
          </span>
        </div>
      </div>

      {showDetails && (
        <>
          {/* Stats Grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '12px',
              marginBottom: '20px',
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
              }}
            >
              <div style={{ color: '#667eea', marginBottom: '8px' }}>
                <BookOpen size={24} style={{ margin: '0 auto' }} />
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#1a202c' }}>
                {progress.storiesCompleted}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>
                Completed
              </div>
            </div>

            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
              }}
            >
              <div style={{ color: '#764ba2', marginBottom: '8px' }}>
                <TrendingUp size={24} style={{ margin: '0 auto' }} />
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#1a202c' }}>
                {Math.round(progress.completionPercentage)}%
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>
                Overall
              </div>
            </div>

            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
              }}
            >
              <div style={{ color: '#10b981', marginBottom: '8px' }}>
                <Clock size={24} style={{ margin: '0 auto' }} />
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#1a202c' }}>
                {Math.floor(progress.timeSpent / 60)}h {progress.timeSpent % 60}m
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>
                Time Spent
              </div>
            </div>
          </div>

          {/* Motivation Message */}
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '0.9375rem', color: '#64748b', lineHeight: 1.6 }}>
              {getMotivationalMessage(progress)}
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.9;
          }
        }
        
        @keyframes shimmer {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }
      `}</style>
    </div>
  );
}

function getMotivationalMessage(progress: UserProgress): string {
  if (progress.storiesCompleted === 0) {
    return "Begin your bilingual learning journey! Every story brings new discoveries.";
  }
  if (progress.storiesCompleted < 5) {
    return "You're building strong foundations! Keep exploring and learning.";
  }
  if (progress.storiesCompleted < 10) {
    return "Wonderful progress! Your language skills are growing with each story.";
  }
  if (progress.storiesCompleted < 20) {
    return "Amazing work! You're becoming a confident bilingual learner.";
  }
  if (progress.storiesCompleted < 30) {
    return "Impressive dedication! Your love of learning shines through.";
  }
  return "Outstanding achievement! You're mastering bilingual storytelling!";
}

